import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Divider,
  Alert,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Autocomplete
} from '@mui/material';
import {
  Person,
  Payment,
  CheckCircle,
  Clear,
  SelectAll,
  Deselect
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { formatINR } from '../utils/currency';
import { MethodOptionContent, METHOD_VALUES, StatusIndicator } from '../components/Indicators';

const QuickCollectionMulti = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [paymentDate, setPaymentDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [paymentMonth, setPaymentMonth] = useState(dayjs().format('YYYY-MM'));
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPending, setFilterPending] = useState(true);

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const [usersRes, paymentsRes] = await Promise.all([
        axios.get('/api/users'),
        axios.get('/api/payments')
      ]);

      // Filter out admin users and system admin users from payment operations
      const filteredUsers = usersRes.data
        .filter(user => !user.is_admin && !user.is_system_admin)
        .map(user => {
          const currentMonthPayment = paymentsRes.data.find(
            p => p.user_id === user.id && p.payment_month === paymentMonth
          );
          
          return {
            ...user,
            hasCurrentPayment: !!currentMonthPayment,
            currentPaymentStatus: currentMonthPayment?.status || 'none'
          };
        });

      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = !filterPending || !user.hasCurrentPayment || user.currentPaymentStatus === 'pending';
    
    return matchesSearch && matchesFilter;
  });

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const availableUserIds = filteredUsers
      .filter(user => !user.hasCurrentPayment || user.currentPaymentStatus === 'pending')
      .map(user => user.id);
    setSelectedUsers(availableUserIds);
  };

  const handleDeselectAll = () => {
    setSelectedUsers([]);
  };

  const handleSubmit = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setProcessing(true);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    try {
      // Process payments in parallel batches of 5
      const batchSize = 5;
      for (let i = 0; i < selectedUsers.length; i += batchSize) {
        const batch = selectedUsers.slice(i, i + batchSize);
        
        await Promise.allSettled(
          batch.map(async (userId) => {
            try {
              await axios.post('/api/payments', {
                userId,
                amount: parseFloat(amount),
                paymentMethod,
                paymentDate,
                paymentMonth,
                notes,
                status: 'completed'
              });
              successCount++;
            } catch (error) {
              failCount++;
              const userName = users.find(u => u.id === userId)?.full_name || `User ${userId}`;
              errors.push(`${userName}: ${error.response?.data?.error || error.message}`);
            }
          })
        );
      }

      // Show results
      if (successCount > 0) {
        toast.success(`Successfully created ${successCount} payment${successCount > 1 ? 's' : ''}`);
      }
      
      if (failCount > 0) {
        const errorMessage = `Failed to create ${failCount} payment${failCount > 1 ? 's' : ''}`;
        toast.error(errorMessage);
        console.error('Payment creation errors:', errors);
      }

      if (successCount > 0) {
        onSuccess?.();
        handleClose();
      }

    } catch (error) {
      console.error('Batch payment creation error:', error);
      toast.error('Failed to process payments');
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    if (!processing) {
      setSelectedUsers([]);
      setAmount('');
      setNotes('');
      setSearchTerm('');
      onClose();
    }
  };

  const selectedUsersData = users.filter(user => selectedUsers.includes(user.id));
  const totalAmount = selectedUsers.length * parseFloat(amount || 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={0.5}>
          <Typography variant="h6">Quick Multi-Collection</Typography>
          <Box display="flex" gap={{ xs: 0.5, sm: 1 }}>
            <Button
              size="small"
              startIcon={<SelectAll />}
              onClick={handleSelectAll}
              disabled={processing}
            >
              {isPhone ? 'All available' : 'Select All Available'}
            </Button>
            <Button
              size="small"
              startIcon={<Deselect />}
              onClick={handleDeselectAll}
              disabled={processing}
            >
              {isPhone ? 'Clear' : 'Clear Selection'}
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Payment Details */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Payment Details</Typography>
                
                <TextField
                  label="Amount per User"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  disabled={processing}
                  InputProps={{
                    startAdornment: '₹'
                  }}
                  inputProps={{ inputMode: 'decimal' }}
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    disabled={processing}
                  >
                    {METHOD_VALUES.map(m => (
                      <MenuItem key={m} value={m}><MethodOptionContent method={m} /></MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Payment Date"
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  fullWidth
                  margin="normal"
                  disabled={processing}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="Payment Month"
                  type="month"
                  value={paymentMonth}
                  onChange={(e) => setPaymentMonth(e.target.value)}
                  fullWidth
                  margin="normal"
                  disabled={processing}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="Notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                  disabled={processing}
                  placeholder="e.g., Monthly collection, Group payment"
                />

                {selectedUsers.length > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>{selectedUsers.length}</strong> users selected
                      <br />
                      Total amount: <strong>{formatINR(totalAmount)}</strong>
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* User Selection */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
                  <Typography variant="h6">Select Users</Typography>
                  <Box display="flex" gap={1} alignItems="center" sx={{ ml: { xs: -1.25, sm: 0 } }}>
                    <Checkbox
                      checked={filterPending}
                      onChange={(e) => setFilterPending(e.target.checked)}
                      disabled={processing}
                    />
                    <Typography variant="body2">Show only pending/unpaid</Typography>
                  </Box>
                </Box>

                <TextField
                  label="Search users"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  margin="normal"
                  disabled={processing}
                  placeholder="Search by name or username"
                />

                {/* Phone: the dialog scrolls, no second scroller inside it */}
                <List sx={{ maxHeight: { xs: 'none', md: 300 }, overflow: { xs: 'visible', md: 'auto' } }}>
                  {filteredUsers.map((user) => {
                    const isSelected = selectedUsers.includes(user.id);
                    const canSelect = !user.hasCurrentPayment || user.currentPaymentStatus === 'pending';
                    
                    return (
                      <ListItem
                        key={user.id}
                        dense
                        button
                        onClick={() => canSelect && handleUserToggle(user.id)}
                        disabled={!canSelect || processing}
                        sx={{
                          bgcolor: isSelected ? 'action.selected' : 'inherit',
                          '&:hover': {
                            bgcolor: canSelect ? 'action.hover' : 'inherit'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: { xs: 44, sm: 56 } }}>
                          <Checkbox
                            checked={isSelected}
                            disabled={!canSelect || processing}
                            onChange={() => canSelect && handleUserToggle(user.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </ListItemIcon>
                        {!isPhone && (
                          <ListItemIcon>
                            <Person />
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={user.full_name}
                          secondary={
                            <Box display="flex" alignItems="center" flexWrap="wrap" columnGap={1}>
                              <span>@{user.username}</span>
                              {user.hasCurrentPayment && (
                                <StatusIndicator status={user.currentPaymentStatus} size="small" />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    );
                  })}
                  
                  {filteredUsers.length === 0 && !loading && (
                    <ListItem>
                      <ListItemText
                        primary="No users found"
                        secondary="Try adjusting your search or filter criteria"
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {processing && (
          <Box mt={2}>
            <LinearProgress />
            <Typography variant="body2" align="center" mt={1}>
              Processing payments... Please wait.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={processing}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={selectedUsers.length === 0 || !amount || processing}
          startIcon={<Payment />}
          sx={{ flex: { xs: 1, sm: '0 0 auto' }, whiteSpace: 'nowrap' }}
        >
          {processing ? 'Processing...' : `Create ${selectedUsers.length} Payment${selectedUsers.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickCollectionMulti;